import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  listFrameworks,
  listQuantitativeKpis,
  listKpiFrequencies,
} from "./kpiFrequencyApi";
import { mergeSavedMaps, safeParseJSON, scopesFromKey, scopesKey } from "./helpers";

/**
 * Normalises reporting questions down to what this screen renders.
 * @param {object[]} questions raw questions from getReportingQuestionnew.
 * @returns {Array<{kpiId: number, title: string, tags: *, moduleName: string|null}>} the KPI rows.
 */
function normaliseKpis(questions) {
  return questions.map((q) => ({
    kpiId: q.questionId,
    title: q.title,
    tags: q.tags,
    moduleName: q.moduleName ?? null,
  }));
}

/**
 * Each selected site's overrides, in selection order.
 *
 * The endpoint answers in the order the sites were asked for, which is what lets
 * the page index a site's map by its position in the selection. A short or absent
 * list is padded rather than trusted: a mismatch would otherwise slide every
 * site's cadences onto its neighbour, which reads as real data rather than as a
 * failed fetch.
 * @param {Array<{kpiFrequencyMap: object}>} results the endpoint's per-site results.
 * @param {number} expected how many sites were asked for.
 * @returns {object[]} one `{ [kpiId]: frequency }` map per selected site.
 */
function scopeMapsFromResults(results, expected) {
  const list = Array.isArray(results) ? results : [];
  return Array.from({ length: expected }, (_, index) => list[index]?.kpiFrequencyMap || {});
}

/**
 * Owns everything the page fetches for one financial year and the sites selected.
 *
 * - `loading` is true only for blocking loads (first load / FY or site switch) so
 *   the page can show a spinner without unmounting anything on later refreshes.
 * - `refresh()` re-fetches in the background after a save; rows keep their
 *   selection and scroll position and simply re-render with fresh data.
 * - Responses are tagged with a fetch id; anything that resolves after a newer
 *   fetch started is dropped, so a slow response can't clobber a newer FY or site.
 * - Every selected site is read in one request, and the results are folded into
 *   one map so a KPI the sites disagree on reads as mixed rather than as
 *   whichever site answered last. One request rather than one per site: a
 *   forty-location company would otherwise open the page with forty parallel
 *   calls.
 * - That same response carries the company-wide baseline — the cadence a site
 *   with no setting of its own falls back to — plus the financial-year default
 *   and fiscal start month, which belong to the year rather than to any one site.
 * - With no site selected nothing is fetched: there is nothing to show cadences
 *   for, and the page says so instead of loading a list nobody can edit.
 * @param {number} fyId the financial year to load.
 * @param {Array<{locationId: number, subLocationId: number}>} scopes the sites to load.
 * @returns {object} KPI rows, the selected sites' merged overrides, each site's
 *                   own overrides in selection order, the baseline map, the FY
 *                   default, the fiscal start month, and the load-state controls.
 */
export function useKpiFrequencyData(fyId, scopes) {
  const [kpis, setKpis] = useState([]);
  const [savedMap, setSavedMap] = useState({});
  const [scopeMaps, setScopeMaps] = useState([]);
  const [baselineMap, setBaselineMap] = useState({});
  const [defaultFrequency, setDefaultFrequency] = useState("YEARLY");
  const [startMonth, setStartMonth] = useState(4);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);

  const selectionKey = scopesKey(scopes);
  const selection = useMemo(() => scopesFromKey(selectionKey), [selectionKey]);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!fyId || !selection.length) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    const fetchId = ++fetchIdRef.current;
    const isCurrent = () => fetchId === fetchIdRef.current;

    if (background) setRefreshing(true);
    else setLoading(true);

    try {
      const userId = safeParseJSON(localStorage.getItem("user_temp_id"));
      const [frequencyRes, frameworkRes] = await Promise.all([
        listKpiFrequencies(fyId, selection),
        listFrameworks(userId),
      ]);

      if (!frequencyRes?.isSuccess) {
        throw new Error(frequencyRes?.message || "Frequency fetch failed");
      }

      const frameworks = frameworkRes?.data?.data || [];
      const kpiRes = await listQuantitativeKpis(fyId, frameworks.map((f) => f.id));
      const questions = kpiRes?.data?.data || [];

      if (!isCurrent()) return;


      const payload = frequencyRes.data?.data || {};
      const maps = scopeMapsFromResults(payload.results, selection.length);
      setKpis(normaliseKpis(questions));
      setSavedMap(mergeSavedMaps(maps));
      setScopeMaps(maps);
      setBaselineMap(payload.baseline?.kpiFrequencyMap || {});
      setDefaultFrequency(payload.defaultFrequency || "YEARLY");
      setStartMonth(payload.startMonth || 4);
      setError(null);
    } catch (e) {
      console.error(e);
      if (isCurrent()) {
        setError("Failed to load KPI frequencies. Please try again.");
      }
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [fyId, selection]);

  useEffect(() => {
    load();
  }, [load]);

  /** Background refresh for after-save updates — never blanks the page. */
  const refresh = useCallback(() => load({ background: true }), [load]);

  /** Blocking reload for the error-state Retry button. */
  const reload = useCallback(() => load(), [load]);

  return {
    kpis,
    savedMap,
    scopeMaps,
    baselineMap,
    defaultFrequency,
    startMonth,
    loading,
    refreshing,
    error,
    refresh,
    reload,
  };
}
